import { isOroborosProductPath } from '../oroboros/routes';
import { isWalletPrincipalIdString } from './walletPrincipal';

const ONBOARDING_PATH = '/onboarding';

type HyperliquidOnboardingRedirectOptions = {
  allowMiniApp?: boolean;
  source?: string;
  action?: string;
  /** Auto-start the onboarding spotlight tour on arrival. */
  onboardingTour?: boolean;
};

export function isOnHyperliquidOnboardingRoute(): boolean {
  try {
    const path = (window.location.pathname || '').toLowerCase();
    return path === ONBOARDING_PATH || path.startsWith(`${ONBOARDING_PATH}/`);
  } catch {
    return false;
  }
}

export function shouldSkipHyperliquidOnboardingRedirect(
  options: HyperliquidOnboardingRedirectOptions = {},
): boolean {
  if (isOnHyperliquidOnboardingRoute()) return true;
  try {
    const path = (window.location.pathname || '').toLowerCase();
    // The MiniApp owns its own setup state. A background auth refresh must not
    // throw TG-backed users out of the app and into the onboarding wizard.
    if (!options.allowMiniApp && (path === '/miniapp' || path.startsWith('/miniapp/'))) return true;
    // Oroboros hosts HL setup in OroborosOnboardingOverlay — never hard-redirect
    // away from the terminal after wallet sign-in.
    if (isOroborosProductPath(path)) return true;
  } catch {}
  try {
    const params = new URLSearchParams(window.location.search || '');
    if (params.get('guest') === '1') return true;
  } catch {}
  return false;
}

/** Navigate to the HL onboarding wizard when setup is incomplete. */
export function redirectToHyperliquidOnboarding(
  options: HyperliquidOnboardingRedirectOptions = {},
): boolean {
  if (shouldSkipHyperliquidOnboardingRedirect(options)) return false;
  try {
    const url = new URL(ONBOARDING_PATH, window.location.origin);
    if (options.source) url.searchParams.set('source', options.source);
    if (options.action) url.searchParams.set('action', options.action);
    if (options.onboardingTour) url.searchParams.set('onboarding_tour', '1');
    window.location.replace(url.toString());
    return true;
  } catch {
    const params = new URLSearchParams();
    if (options.source) params.set('source', options.source);
    if (options.action) params.set('action', options.action);
    if (options.onboardingTour) params.set('onboarding_tour', '1');
    const query = params.toString();
    window.location.href = `${ONBOARDING_PATH}${query ? `?${query}` : ''}`;
    return true;
  }
}

export function maybeRedirectToHyperliquidOnboarding(
  requires?: boolean | null,
  options: HyperliquidOnboardingRedirectOptions = {},
): boolean {
  if (!requires) return false;
  return redirectToHyperliquidOnboarding(options);
}

/** Wallet-only first-timers with no prior HL keys — redirect when server says onboarding is required. */
export function maybeRedirectWalletOnlyToHyperliquidOnboarding(
  principalId: string | number | null | undefined,
  requires?: boolean | null,
  options: HyperliquidOnboardingRedirectOptions = {},
): boolean {
  if (!requires) return false;
  const idStr = principalId != null ? String(principalId) : '';
  if (!isWalletPrincipalIdString(idStr)) return false;
  return redirectToHyperliquidOnboarding(options);
}
